import React, { useState, useEffect } from "react";
import { Download, ChevronDown, ChevronUp } from "react-feather";
import { Table, Button, Dropdown, Menu, Switch, Popover } from "antd";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import moment from "moment-timezone";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import exportService from '../../utils/exportService';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

const DailyFlashReport = () => {
  const { t } = useTranslation();
  const currency = localStorage.getItem("currencycode") || "₹";

  const [data, setData] = useState(null);
  const [roomTypeData, setRoomTypeData] = useState([]);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const [shiftData, setShiftData] = useState({
    summary: {
      totalCheckIns: 0,
      totalCheckOuts: 0,
      occupancyPercentage: 0,
      totalRevenue: 0,
    },
    frontOfficeActivities: {
      guestMovements: {
        checkIns: 0,
        checkOuts: 0,
        noShows: 0,
        cancellations: 0,
        stayover: 0,
      },
      roomStatus: {
        available: 0,
        occupied: 0,
        outOfOrder: 0,
      },
    },
    financialSummary: {
      payments: [
        { type: "Cash", amount: 0 },
        { type: "Credit Card", amount: 0 },
        { type: "Online", amount: 0 },
      ],
      refundsAdjustments: 0,
      pendingPayments: 0,
    },
    additionalCharges: [], // New field for dynamic miscellaneous charges
    taxTypes: [], // New field for dynamic tax types
  });
  const [loading, setLoading] = useState({
    dailyFlash: false,
    roomTypeWise: false,
    shiftEndSummary: false,
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const businessDate = localStorage.getItem("business_date");
    const today = moment().tz("Asia/Kolkata").startOf("day");
    const yesterday = today.clone().subtract(1, "days");
    return businessDate && moment(businessDate).isValid()
      ? moment(businessDate).tz("Asia/Kolkata").subtract(1, "days").toDate()
      : yesterday.toDate();
  });
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [showGraph, setShowGraph] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    roomRevenue: true,
    foodAndBeverages: true,
    miscellaneous: true,
    tax: true,
    guestMovements: true,
    financialSummary: true,
  });

  // Fetch Daily Flash Report
  const fetchDailyFlashReport = async () => {
    setLoading((prev) => ({ ...prev, dailyFlash: true }));
    const token = localStorage.getItem("token");

    if (!moment(selectedDate).isValid()) {
      Swal.fire({
        title: t("error"),
        text: t("invalid_date_selected"),
        icon: "error",
      });
      setData(null);
      setLoading((prev) => ({ ...prev, dailyFlash: false }));
      return;
    }

    try {
      const response = await axios.get(`${apiURL}/reports/daily-flash`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          businessDate: moment(selectedDate).format("YYYY-MM-DD"),
        },
      });
      if (response.data.success) {
        setData(response.data.report || null);
      } else {
        throw new Error(t("failed_fetch_daily_flash_report"));
      }
    } catch (error) {

      setData(null);
    } finally {
      setLoading((prev) => ({ ...prev, dailyFlash: false }));
    }
  };

  // Fetch Room Type Wise Data
  const fetchRoomTypeWiseData = async () => {
    setLoading((prev) => ({ ...prev, roomTypeWise: true }));
    const token = localStorage.getItem("token");

    if (!moment(selectedDate).isValid()) {
      console.error("Invalid selected date:", selectedDate);
      setLoading((prev) => ({ ...prev, roomTypeWise: false }));
      return;
    }

    try {
      const response = await axios.get(`${apiURL}/reports-daily/room-type-wise`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: moment(selectedDate).format("YYYY-MM-DD"),
          endDate: moment(selectedDate).format("YYYY-MM-DD"),
          page: 1,
          pageSize: 100,
        },
      });

      if (response.data.success) {
        setRoomTypeData(response.data.report || []);
        setApplicableTaxes(response.data.applicableTaxes || []);
      } else {
        throw new Error(response.data.message || t("failed_fetch_room_type_wise"));
      }
    } catch (error) {
      console.error("Error fetching Room Type Wise Report:", error.message);
      Swal.fire({
        title: t("error"),
        text: t("failed_fetch_room_type_wise"),
        icon: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, roomTypeWise: false }));
    }
  };

  // Fetch Shift End Summary
  const fetchShiftEndSummary = async () => {
    setLoading((prev) => ({ ...prev, shiftEndSummary: true }));
    const token = localStorage.getItem("token");
    const companyid = localStorage.getItem("companyid");
    const businessDate = moment(selectedDate).format("YYYY-MM-DD");

    if (!token) {
      throw new Error("No authentication token found.");
    }
    if (!companyid || isNaN(parseInt(companyid))) {
      throw new Error("Valid companyid is required.");
    }
    if (!moment(businessDate).isValid()) {
      throw new Error("Invalid business date.");
    }

    try {
      const response = await axios.get(`${apiURL}/reports-daily/day-end-summary`, {
        params: { businessDate },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success && response.data.report) {
        const { report } = response.data;
        const summary = typeof report.summary === "string" ? JSON.parse(report.summary) : report.summary;

        setShiftData({
          summary: {
            totalCheckIns: summary.checked_in || 0,
            totalCheckOuts: summary.checked_out || 0,
            occupancyPercentage:
              report.occupied_rooms && report.occupied_rooms + report.vacant_rooms > 0
                ? (report.occupied_rooms / (report.occupied_rooms + report.vacant_rooms)) * 100
                : 0,
            totalRevenue: summary.room_revenue || 0,
          },
          frontOfficeActivities: {
            guestMovements: {
              checkIns: summary.checked_in || 0,
              checkOuts: summary.checked_out || 0,
              noShows: summary.no_show || 0,
              cancellations: summary.cancelled || 0,
              stayover: summary.stayover || 0,
            },
            roomStatus: {
              available: report.vacant_rooms || 0,
              occupied: report.occupied_rooms || 0,
              outOfOrder: report.maintenance_rooms || 0,
            },
          },
          financialSummary: {
            payments: [
              { type: "Cash", amount: summary.cash_collection || 0 },
              { type: "Credit Card", amount: summary.bank_card_collection || 0 },
              { type: "Online", amount: (summary.upi_collection || 0) + (summary.bank_transfer_collection || 0) },
            ],
            refundsAdjustments: summary.expenses || 0,
            pendingPayments: 0,
          },
          additionalCharges: report.summary.additional_charges_by_type?.charges || [],
          taxTypes: report.summary.additional_charges_by_type?.tax_types || [],
        });
      } else {
        throw new Error(response.data.message || t("failed_fetch_shift_end_summary"));
      }
    } catch (error) {
      console.error("Error fetching Shift End Summary:", error);
      Swal.fire({
        title: t("error"),
        text: error.message || t("failed_fetch_shift_end_summary"),
        icon: "error",
      });
      setShiftData({
        summary: {
          totalCheckIns: 0,
          totalCheckOuts: 0,
          occupancyPercentage: 0,
          totalRevenue: 0,
        },
        frontOfficeActivities: {
          guestMovements: {
            checkIns: 0,
            checkOuts: 0,
            noShows: 0,
            cancellations: 0,
            stayover: 0,
          },
          roomStatus: {
            available: 0,
            occupied: 0,
            outOfOrder: 0,
          },
        },
        financialSummary: {
          payments: [
            { type: "Cash", amount: 0 },
            { type: "Credit Card", amount: 0 },
            { type: "Online", amount: 0 },
          ],
          refundsAdjustments: 0,
          pendingPayments: 0,
        },
        additionalCharges: [],
        taxTypes: [],
      });
    } finally {
      setLoading((prev) => ({ ...prev, shiftEndSummary: false }));
    }
  };

  // Handle date change for prev/next buttons
  const handleDateChange = (action) => {
    let newDate;
    if (action === "prev") {
      newDate = moment(selectedDate).subtract(1, "days").toDate();
    } else if (action === "next") {
      newDate = moment(selectedDate).add(1, "days").toDate();
    }
    if (moment(newDate).isAfter(moment().tz("Asia/Kolkata").subtract(1, "days"))) {
      Swal.fire({
        title: t("error"),
        text: t("date_cannot_be_current_or_future"),
        icon: "error",
      });
      return;
    }
    setSelectedDate(newDate);
  };

  useEffect(() => {
    fetchDailyFlashReport();
    fetchRoomTypeWiseData();
    fetchShiftEndSummary();
  }, [moment(selectedDate).format("YYYY-MM-DD")]);

  const sampleData = {
    totalRooms: 16,
    roomsSold: 0,
    occupancyPercentage: 0,
    arr: 0.00,
    roomRevenue: [
      { type: "AC Deluxe", roomsSold: 0, complRoom: 0, total: 0, grossRev: 0.00, commDiscount: 0.00, netRev: 0.00 },
      { type: "Fam Deluxe", roomsSold: 0, complRoom: 0, total: 0, grossRev: 0.00, commDiscount: 0.00, netRev: 0.00 },
      { type: "Fam Huts", roomsSold: 0, complRoom: 0, total: 0, grossRev: 0.00, complNonRoom: 0.00, netRev: 0.00 },
      { type: "Super Deluxe", roomsSold: 0, complRoom: 0, total: 0, grossRev: 0.00, complNonRoom: 0.00, netRev: 0.00 },
    ],
    foodAndBeverages: {
      restaurant: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
      pos2: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
      pos3: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
      pos4: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
      pos5: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
      pos6: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
      total: { taxable: 0.00, nonTaxable: 0.00, subTotal: 0.00, lessDiscount: 0.00, grandTotal: 0.00 },
    },
    miscellaneous: [
      { particular: "Sub Total", revenue: 0.00, tax_amount: 0.00, remarks: "" },
      { particular: "Other/Extra Chgs.", revenue: 0.00, tax_amount: 0.00, remarks: "" },
      { particular: "Extra Bed", revenue: 0.00, tax_amount: 0.00, remarks: "" },
    ],
    tax: {
      frontOffice: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      restaurant: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      pos2: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      pos3: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      pos4: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      pos5: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      pos6: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
      total: { totalTax: 0.00, serviceCharge: 0.00, subTotal: 0.00 },
    },
    grandTotal: 0.00,
  };

  const reportData = data || sampleData;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const filteredData = {
    ...reportData,
    roomRevenue: categoryFilter && categoryFilter.value !== "roomRevenue" ? [] : roomTypeData,
    foodAndBeverages: categoryFilter && categoryFilter.value !== "foodAndBeverages" ? { ...reportData.foodAndBeverages, total: { taxable: 0, nonTaxable: 0, subTotal: 0, lessDiscount: 0, grandTotal: 0 } } : reportData.foodAndBeverages,
    miscellaneous: categoryFilter && categoryFilter.value !== "miscellaneous" ? [] : shiftData.additionalCharges.map(charge => ({
      particular: charge.charge_name,
      revenue: charge.amount,
      tax_amount: charge.tax_amount,
      remarks: "",
      ...charge.tax_breakdown,
    })),
    tax: categoryFilter && categoryFilter.value !== "tax" ? { ...reportData.tax, total: { totalTax: 0, serviceCharge: 0, subTotal: 0 } } : reportData.tax,
  };

  const totalTaxCollection = filteredData.roomRevenue.reduce((sum, row) => 
    sum + applicableTaxes.reduce((taxSum, taxName) => taxSum + (row[taxName] || 0), 0), 0
  ) + filteredData.miscellaneous.reduce((sum, row) => sum + (row.tax_amount || 0), 0);

const activityStats = [
  { label: t("total_check_ins"), value: shiftData.summary.totalCheckIns, color: "#28a745" },
  { label: t("total_check_outs"), value: shiftData.summary.totalCheckOuts, color: "#007bff" },
  { label: t("occupancy_percentage"), value: `${shiftData.summary.occupancyPercentage.toFixed(2)}%`, color: "#17a2b8" },
  { label: t("total_revenue"), value: `${currency}${shiftData.summary.totalRevenue.toFixed(2)}`, color: "#dc3545" },
  { label: t("tax_collection"), value: `${currency}${totalTaxCollection.toFixed(2)}`, color: "#ffc107" },
  { label: t("net_revenue_with_tax"), value: `${currency}${(shiftData.summary.totalRevenue + totalTaxCollection).toFixed(2)}`, color: "#28a745" },
];

const barChartData = {
  labels: [t("room_revenue"), t("food_and_beverages"), t("miscellaneous"), t("tax")],
  datasets: [
    {
      label: t("revenue_contribution"),
      data: [
        Number(filteredData.roomRevenue.reduce((sum, row) => sum + (Number(row.net_revenue) || 0), 0)) || 0,
        Number(filteredData.foodAndBeverages?.total?.grandTotal || 0),
        Number(filteredData.miscellaneous.reduce((sum, row) => sum + (Number(row.revenue) || 0), 0)) || 0,
        Number(totalTaxCollection || 0),
      ],
      backgroundColor: ["#007bff", "#28a745", "#dc3545", "#ffc107"],
      borderColor: ["#005bb5", "#1e7e34", "#a71d2a", "#d39e00"],
      borderWidth: 1,
    },
  ],
};

const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: {
        font: {
          size: 10,
        },
        padding: 10,
      },
    },
    tooltip: {
      bodyFont: {
        size: 10,
      },
      callbacks: {
        label: (context) => {
          const value = Number(context.raw) || 0; // Ensure value is numeric
          const total = context.dataset.data.reduce((sum, val) => sum + (Number(val) || 0), 0);
          const percentage = total ? ((value / total) * 100).toFixed(1) : 0;
          return `${context.label}: ${currency}${value.toFixed(2)} (${percentage}%)`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: t("amount"),
        font: {
          size: 10,
        },
      },
      ticks: {
        font: {
          size: 11,
        },
        padding: 5,
      },
    },
    x: {
      title: {
        display: true,
        text: t("categories"),
        font: {
          size: 10,
        },
      },
      ticks: {
        font: {
          size: 10,
        },
        padding: 5,
      },
    },
  },
  layout: {
    padding: {
      top: 10,
      bottom: 10,
      left: 10,
      right: 10,
    },
  },
  elements: {
    bar: {
      barThickness: 30,
    },
  },
};
  const handleCategoryFilterChange = (option) => {
    setCategoryFilter(option);
  };

  const columns = [
    {
      title: t("room_type"),
      dataIndex: "room_type",
      key: "room_type",
      sorter: (a, b) => a.room_type.localeCompare(b.room_type),
    },
    {
      title: t("room_sold_count"),
      dataIndex: "room_sold_count",
      key: "room_sold_count",
      sorter: (a, b) => a.room_sold_count - b.room_sold_count,
    },
    {
      title: t("actual_price"),
      dataIndex: "actual_price",
      key: "actual_price",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.actual_price - b.actual_price,
    },
    {
      title: t("commission_discount"),
      dataIndex: "discount",
      key: "discount",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.discount - b.discount,
    },
    ...applicableTaxes.map((taxName) => ({
      title: `${taxName} (${currency})`,
      dataIndex: taxName,
      key: taxName,
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
    })),
    {
      title: t("total_tax"),
      key: "total_tax",
      render: (_, record) => `${currency}${applicableTaxes.reduce((sum, taxName) => sum + (record[taxName] || 0), 0).toFixed(2)}`,
      sorter: (a, b) => applicableTaxes.reduce((sum, taxName) => sum + (a[taxName] || 0), 0) - applicableTaxes.reduce((sum, taxName) => sum + (b[taxName] || 0), 0),
    },
    {
      title: t("net_revenue"),
      dataIndex: "net_revenue",
      key: "net_revenue",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.net_revenue - b.net_revenue,
      onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
    },
  ];

const miscColumns = [
  { title: t("particular"), dataIndex: "particular", key: "particular" },
  { title: t("revenue"), dataIndex: "revenue", key: "revenue", render: (value) => `${currency}${Number(value || 0).toFixed(2)}`, sorter: (a, b) => a.revenue - b.revenue },
  ...shiftData.taxTypes.map((taxName) => ({
    title: `${taxName} (${currency})`,
    dataIndex: taxName,
    key: taxName,
    render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
    sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
  })),
  { title: t("total_tax"), dataIndex: "tax_amount", key: "tax_amount", render: (value) => `${currency}${Number(value || 0).toFixed(2)}`, sorter: (a, b) => a.tax_amount - b.tax_amount },
  { title: t("net_revenue"), dataIndex: "net_revenue", key: "net_revenue", render: (text) => `${currency}${Number(text || 0).toFixed(2)}`, sorter: (a, b) => a.net_revenue - b.net_revenue },
  { title: t("remarks"), dataIndex: "remarks", key: "remarks" },
];

// Calculate totals from roomTypeData with enhanced debug logging
console.log("roomTypeData before reduce:", roomTypeData); // Log the full dataset
const totals = roomTypeData.reduce(
  (acc, item) => {
    console.log("Processing item:", item); // Log each item with all fields
    const totalTax = applicableTaxes.reduce((sum, taxName) => sum + (Number(item[taxName]) || 0), 0);
    const computedNetRevenue = (Number(item.actual_price) || 0) - (Number(item.discount) || 0) + totalTax;
    return {
      room_sold_count: acc.room_sold_count + (Number(item.room_sold_count) || 0),
      actual_price: acc.actual_price + (Number(item.actual_price) || 0),
      discount: acc.discount + (Number(item.discount) || 0),
      net_revenue: acc.net_revenue + computedNetRevenue,
      total_tax: acc.total_tax + totalTax,
      ...applicableTaxes.reduce((taxAcc, taxName) => ({
        ...taxAcc,
        [taxName]: (taxAcc[taxName] || 0) + (Number(item[taxName]) || 0),
      }), {}),
    };
  },
  {
    room_sold_count: 0,
    actual_price: 0,
    discount: 0,
    net_revenue: 0,
    total_tax: 0,
    ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
  }
);
console.log("totals after reduce:", totals); // Log the final totals

// Add late check-out fees to room revenue totals if present


const miscTotals = filteredData.miscellaneous.reduce(
  (acc, item) => {
    console.log("Processing item:", item); // Log each item
    const totalTax = (Number(item.tax_amount) || 0) + shiftData.taxTypes.reduce((sum, taxName) => sum + (Number(item[taxName]) || 0), 0);
    const computedNetRevenue = (Number(item.revenue) || 0) + totalTax;
    return {
      revenue: acc.revenue + (Number(item.revenue) || 0),
      tax_amount: acc.tax_amount + (Number(item.tax_amount) || 0),
      net_revenue: acc.net_revenue + computedNetRevenue,
      ...shiftData.taxTypes.reduce((taxAcc, taxName) => ({
        ...taxAcc,
        [taxName]: (taxAcc[taxName] || 0) + (Number(item[taxName]) || 0),
      }), {}),
    };
  },
  {
    revenue: 0,
    tax_amount: 0,
    net_revenue: 0,
    ...shiftData.taxTypes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
  }
);

  const exportData = [
    ...filteredData.roomRevenue.map((row, index) => ({
      section: t("room_revenue"),
      row: index + 1,
      type: row.room_type,
      roomsSold: row.room_sold_count,
      actual_price: row.actual_price,
      commission_discount: row.discount,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: row[taxName] || 0 }), {}),
      total_tax: applicableTaxes.reduce((sum, taxName) => sum + (row[taxName] || 0), 0),
      net_revenue: row.net_revenue,
    })),
    ...Object.keys(filteredData.foodAndBeverages)
      .filter((key) => key !== "total")
      .map((pos, index) => ({
        section: t("food_and_beverages"),
        row: index + 1,
        particular: pos.charAt(0).toUpperCase() + pos.slice(1),
        taxable: filteredData.foodAndBeverages[pos].taxable,
        nonTaxable: filteredData.foodAndBeverages[pos].nonTaxable,
        subTotal: filteredData.foodAndBeverages[pos].subTotal,
        lessDiscount: filteredData.foodAndBeverages[pos].lessDiscount,
        grandTotal: filteredData.foodAndBeverages[pos].grandTotal,
      })),
    {
      section: t("food_and_beverages"),
      row: "Total",
      particular: "Total",
      taxable: filteredData.foodAndBeverages.total.taxable,
      nonTaxable: filteredData.foodAndBeverages.total.nonTaxable,
      subTotal: filteredData.foodAndBeverages.total.subTotal,
      lessDiscount: filteredData.foodAndBeverages.total.lessDiscount,
      grandTotal: filteredData.foodAndBeverages.total.grandTotal,
    },
    ...filteredData.miscellaneous.map((row, index) => ({
      section: t("miscellaneous"),
      row: index + 1,
      particular: row.particular,
      revenue: row.revenue,
      ...shiftData.taxTypes.reduce((acc, taxName) => ({ ...acc, [taxName]: row[taxName] || 0 }), {}),
      tax_amount: row.tax_amount,
      remarks: row.remarks,
    })),
    ...Object.keys(filteredData.tax)
      .filter((key) => key !== "total")
      .map((pos, index) => ({
        section: t("tax"),
        row: index + 1,
        particular: pos.charAt(0).toUpperCase() + pos.slice(1),
        totalTax: filteredData.tax[pos].totalTax,
        serviceCharge: filteredData.tax[pos].serviceCharge,
        subTotal: filteredData.tax[pos].subTotal,
      })),
    {
      section: t("tax"),
      row: "Total",
      particular: "Total",
      totalTax: filteredData.tax.total.totalTax,
      serviceCharge: filteredData.tax.total.serviceCharge,
      subTotal: filteredData.tax.total.subTotal,
    },
    {
      section: t("grand_total"),
      row: 1,
      particular: t("grand_total"),
      value: filteredData.grandTotal,
    },
    ...Object.keys(shiftData.frontOfficeActivities.guestMovements).map((key, index) => ({
      section: t("guest_movements"),
      row: index + 1,
      particular: t(key),
      value: shiftData.frontOfficeActivities.guestMovements[key],
    })),
    ...shiftData.financialSummary.payments.map((payment, index) => ({
      section: t("financial_summary_payments"),
      row: index + 1,
      particular: payment.type,
      value: `${currency}${payment.amount.toFixed(2)}`,
    })),
    {
      section: t("financial_summary"),
      row: 1,
      particular: t("refunds_adjustments"),
      value: `${currency}${shiftData.financialSummary.refundsAdjustments.toFixed(2)}`,
    },
    {
      section: t("financial_summary"),
      row: 2,
      particular: t("pending_payments"),
      value: `${currency}${shiftData.financialSummary.pendingPayments.toFixed(2)}`,
    },
  ];

  const exportHeaders = [
    { key: "section", label: t("section") },
    { key: "row", label: t("row") },
    { key: "type", label: t("type") },
    { key: "roomsSold", label: t("rooms_sold") },
    { key: "actual_price", label: t("actual_price") },
    { key: "commission_discount", label: t("commission_discount") },
    ...applicableTaxes.map((taxName) => ({ key: taxName, label: `${taxName} (${currency})` })),
    { key: "total_tax", label: t("total_tax") },
    { key: "net_revenue", label: t("net_revenue") },
    { key: "particular", label: t("particular") },
    { key: "taxable", label: t("taxable") },
    { key: "nonTaxable", label: t("non_taxable") },
    { key: "subTotal", label: t("sub_total") },
    { key: "lessDiscount", label: t("less_discount") },
    { key: "grandTotal", label: t("grand_total") },
    { key: "revenue", label: t("revenue") },
    ...shiftData.taxTypes.map((taxName) => ({ key: taxName, label: `${taxName} (${currency})` })),
    { key: "tax_amount", label: t("total_tax") },
    { key: "remarks", label: t("remarks") },
    { key: "totalTax", label: t("total_tax") },
    { key: "serviceCharge", label: t("service_charge") },
    { key: "value", label: t("value") },
  ];

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => exportService.exportToXLS(exportData, exportHeaders, `daily_flash_report_${moment(selectedDate).format("YYYY-MM-DD")}.xlsx`, "Daily Flash Report")}>
        {t("export_xls")}
      </Menu.Item>
      <Menu.Item key="csv" onClick={() => exportService.exportToCSV(exportData, exportHeaders, `daily_flash_report_${moment(selectedDate).format("YYYY-MM-DD")}.csv`)}>
        {t("export_csv")}
      </Menu.Item>
      <Menu.Item key="pdf" onClick={() => exportService.exportToPDF(exportData, exportHeaders, t("daily_flash_report"), `daily_flash_report_${moment(selectedDate).format("YYYY-MM-DD")}.pdf`)}>
        {t("export_pdf")}
      </Menu.Item>
    </Menu>
  );

  const categoryOptions = [
    { value: "all", label: t("all_categories") },
    { value: "roomRevenue", label: t("room_revenue") },
    { value: "foodAndBeverages", label: t("food_and_beverages") },
    { value: "miscellaneous", label: t("miscellaneous") },
    { value: "tax", label: t("tax") },
    { value: "guestMovements", label: t("guest_movements") },
    { value: "financialSummary", label: t("financial_summary") },
  ];

  const filterPopupContent = (
    <div className="p-4" style={{ width: "250px" }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("filter_by_category")}</label>
        <Select
          value={categoryFilter}
          onChange={handleCategoryFilterChange}
          options={categoryOptions}
          isClearable
          placeholder={t("filter_by_category")}
          styles={{
            container: (provided) => ({ ...provided, width: "100%" }),
            control: (provided) => ({
              ...provided,
              minHeight: "32px",
              borderRadius: "4px",
              borderColor: "#d9d9d9",
              background: "#fff",
            }),
            menu: (provided) => ({ ...provided, width: "100%" }),
          }}
        />
      </div>
    </div>
  );

  return (
<div className="page-wrapper">
  <div className="content container-fluid">
    <div className="daily-flash-report-container">
      <div className="page-header page-top-icons mb-4 px-3 py-2 bg-white border rounded">
        <div className="row align-items-center flex-nowrap">
          <div className="col-auto d-flex align-items-center gap-3">
            <h2
              className="room-title mb-0"
              style={{
                fontSize: "20px",
                fontWeight: 600,
                width: "400px",
                borderBottom: "2px solid #007bff",
                paddingBottom: "4px",
              }}
            >
              {t("daily_flash_report")}
            </h2>
            <div className="d-flex align-items-center">
              <Button
                onClick={() => handleDateChange("prev")}
                className="btn btn-outline-primary btn-sm rounded-circle me-2"
                style={{ width: "34px", height: "34px" }}
                aria-label={t("previous_date")}
              >
                <i className="fas fa-chevron-left"></i>
              </Button>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  const businessDate = localStorage.getItem("business_date")
                    ? moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").toDate()
                    : moment().tz("Asia/Kolkata").subtract(1, "days").toDate();
                  const maxAllowedDate = moment(businessDate).subtract(1, "days").toDate();
                  if (moment(date).isAfter(moment(maxAllowedDate)) || moment(date).isSame(moment(businessDate), "day")) {
                    Swal.fire({
                      title: t("error"),
                      text: t("date_cannot_be_current_or_beyond_business_date"),
                      icon: "error",
                    });
                    return;
                  }
                  setSelectedDate(date);
                }}
                dateFormat="dd MMM yyyy"
                maxDate={localStorage.getItem("business_date")
                  ? moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").toDate()
                  : moment().tz("Asia/Kolkata").subtract(2, "days").toDate()}
                placeholderText={t("select_date")}
                className="form-control text-center fw-bold"
                style={{ height: "38px", fontSize: "15px", width: "220px" }}
                aria-label={t("select_date")}
              />
              <Button
                onClick={() => handleDateChange("next")}
                className="btn btn-outline-primary btn-sm rounded-circle ms-2"
                style={{ width: "34px", height: "34px" }}
                disabled={moment(selectedDate).isSame(
                  localStorage.getItem("business_date")
                    ? moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").toDate()
                    : moment().tz("Asia/Kolkata").subtract(2, "days").toDate(),
                  "day"
                )}
                aria-label={t("next_date")}
              >
                <i className="fas fa-chevron-right"></i>
              </Button>
            </div>
          </div>
          <div className="col-auto ms-auto d-flex align-items-center gap-3">
            <div className="d-flex align-items-center me-2">
              <span style={{ fontSize: "14px", color: "#666", marginRight: "5px" }}>{t("show_graph")}:</span>
              <Switch
                checked={showGraph}
                onChange={(checked) => setShowGraph(checked)}
                aria-label={t("toggle_graph_visibility")}
              />
            </div>
            <Popover
              content={filterPopupContent}
              title={<span className="font-medium">{t("filters")}</span>}
              trigger="click"
              placement="bottomRight"
              overlayStyle={{ width: "250px" }}
            >
              <Button className="btn btn-link p-0" style={{ fontSize: "14px", color: "#007bff" }} aria-label={t("filter")}>
                <i className="fa fa-filter" style={{ fontSize: "16px", marginRight: "5px" }} />
                {t("filter")}
              </Button>
            </Popover>
            <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
              <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }} aria-label={t("download")}>
                <Download size={16} className="me-2" /> {t("download")}
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="row g-4 mb-4" style={{ padding: "5px 10px" }}>
        {activityStats.map((stat, index) => (
          <div
            key={index}
            className="col-md-2 col-6 stats-box"
            style={{
              margin: "0 15px",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              padding: "10px",
              background: "#fff",
            }}
          >
            <div
              className="stats-border"
              style={{ backgroundColor: stat.color }}
            ></div>
            <div className="stats-content">
              <h2 style={{ fontSize: "1.5rem", margin: 0, fontWeight: 600, color: "#666" }}>{stat.value}</h2>
              <p
                style={{
                  margin: 0,
                  color: "#666",
                  fontSize: "0.9rem",
                  textTransform: "capitalize",
                }}
              >
                {stat.label}
              </p>
            </div>
          </div>
        ))}
      </div>
{showGraph && (
  <div className="row mb-4">
    <div className="col-md-12">
      <div
        className="card chart-container"
        style={{
          borderRadius: "0",
          overflowX: "auto",
          border: "1px solid #dee2e6",
          borderBottom: "none",
          margin: "0 auto",
        }}
      >
        <div
          className="card-header"
          style={{
            backgroundColor: "#fff",
            padding: "12px 16px",
            borderBottom: "none",
            position: "relative",
          }}
        >
          <h5
            className="mb-0 d-flex align-items-center"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#333",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {t("revenue_contribution")}
          </h5>
          <div
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "100%",
              height: "4px",
              backgroundColor: "#28a745",
            }}
          />
        </div>
        <div
          className="card-body p-0"
          style={{
            height: "300px", // Reduced height
            position: "relative",
          }}
        >
          {loading.dailyFlash ? (
            <div className="text-center my-5">
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : (
            <Bar
              data={barChartData}
              options={barChartOptions}
              style={{
                maxWidth: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
      </div>
    </div>
  </div>
)}
{(!categoryFilter || categoryFilter.value === "all" || categoryFilter.value === "roomRevenue") && (
  <div className="row g-0 mb-4">
    <div className="col-md-12">
      <div
        className="card"
        style={{
          borderRadius: "0",
          overflowX: "auto",
          border: "1px solid #dee2e6",
          borderBottom: "none",
        }}
      >
        <div
          className="card-header"
          style={{
            backgroundColor: "#fff",
            padding: "12px 16px",
            borderBottom: "none",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => toggleSection("roomRevenue")}
        >
          <h5
            className="mb-0 d-flex justify-content-between align-items-center"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#333",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {t("room_revenue")} (Total Bookings: {totals.room_sold_count || 0})
            {expandedSections.roomRevenue ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </h5>
          <div
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "100%",
              height: "4px",
              backgroundColor: "#007bff",
            }}
          />
        </div>
        {expandedSections.roomRevenue && (
          <div className="card-body p-0">
            {loading.roomTypeWise ? (
              <div className="text-center my-5">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : roomTypeData.length === 0 ? (
              <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt={t("no_data_available")}
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>{t("no_data")}</h3>
                <p>{t("no_room_type_wise_data")}</p>
              </div>
            ) : (
              <Table
                dataSource={roomTypeData.map((row, index) => ({
                  ...row,
                  key: `room-${index}`,
                  net_revenue: (Number(row.actual_price) || 0) - (Number(row.discount) || 0) + applicableTaxes.reduce((sum, taxName) => sum + (Number(row[taxName]) || 0), 0),
                }))}
                columns={columns}
                rowKey="key"
                bordered
                pagination={false}
                className="daybook-table"
                style={{ margin: 0 }}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: 700, fontSize: "15px" }}>
                    <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                    <Table.Summary.Cell>{totals.room_sold_count || 0}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{(totals.actual_price || 0).toFixed(2)}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{(totals.discount || 0).toFixed(2)}</Table.Summary.Cell>
                    {applicableTaxes.map((taxName, index) => (
                      <Table.Summary.Cell key={index}>{currency}{(totals[taxName] || 0).toFixed(2)}</Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell>{currency}{(totals.total_tax || 0).toFixed(2)}</Table.Summary.Cell>
                    <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>{currency}{(totals.net_revenue || 0).toFixed(2)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            )}
          </div>
        )}
      </div>
    </div>
  </div>
)}
      {(!categoryFilter || categoryFilter.value === "all" || categoryFilter.value === "foodAndBeverages") && (
        <div className="row g-0 mb-4">
          <div className="col-md-12">
            <div
              className="card"
              style={{
                borderRadius: "0",
                overflowX: "auto",
                border: "1px solid #dee2e6",
                borderBottom: "none",
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: "#fff",
                  padding: "12px 16px",
                  borderBottom: "none",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("foodAndBeverages")}
              >
                <h5
                  className="mb-0 d-flex justify-content-between align-items-center"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("food_and_beverages")}
                  {expandedSections.foodAndBeverages ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </h5>
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#007bff",
                  }}
                />
              </div>
              {expandedSections.foodAndBeverages && (
                <div className="card-body p-0">
                  {loading.dailyFlash ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <Table
                      dataSource={[
                        { key: "taxable", particular: t("taxable"), ...filteredData.foodAndBeverages },
                        { key: "nonTaxable", particular: t("non_taxable"), ...filteredData.foodAndBeverages },
                        { key: "subTotal", particular: t("sub_total"), ...filteredData.foodAndBeverages },
                        { key: "lessDiscount", particular: t("less_discount"), ...filteredData.foodAndBeverages },
                        { key: "grandTotal", particular: t("grand_total"), ...filteredData.foodAndBeverages },
                      ]}
                      columns={[
                        {
                          title: t("particular"),
                          dataIndex: "particular",
                          key: "particular",
                        },
                        {
                          title: t("restaurant"),
                          key: "restaurant",
                          render: (_, record) => `${currency}${record.restaurant[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS2",
                          key: "pos2",
                          render: (_, record) => `${currency}${record.pos2[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS3",
                          key: "pos3",
                          render: (_, record) => `${currency}${record.pos3[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS4",
                          key: "pos4",
                          render: (_, record) => `${currency}${record.pos4[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS5",
                          key: "pos5",
                          render: (_, record) => `${currency}${record.pos5[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS6",
                          key: "pos6",
                          render: (_, record) => `${currency}${record.pos6[record.key].toFixed(2)}`,
                        },
                        {
                          title: t("total"),
                          key: "total",
                          render: (_, record) => `${currency}${record.total[record.key].toFixed(2)}`,
                        },
                      ]}
                      rowKey={(record) => record.key}
                      bordered
                      pagination={false}
                      className="daybook-table"
                      style={{ margin: 0 }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
{(!categoryFilter || categoryFilter.value === "all" || categoryFilter.value === "miscellaneous") && (
  <div className="row g-0 mb-4">
    <div className="col-md-12">
      <div
        className="card"
        style={{
          borderRadius: "0",
          overflowX: "auto",
          border: "1px solid #dee2e6",
          borderBottom: "none",
        }}
      >
        <div
          className="card-header"
          style={{
            backgroundColor: "#fff",
            padding: "12px 16px",
            borderBottom: "none",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => toggleSection("miscellaneous")}
        >
          <h5
            className="mb-0 d-flex justify-content-between align-items-center"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#333",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {t("miscellaneous")}
            {expandedSections.miscellaneous ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </h5>
          <div
            style={{
              position: "absolute",
              bottom: "0",
              left: "0",
              width: "100%",
              height: "4px",
              backgroundColor: "#007bff",
            }}
          />
        </div>
        {expandedSections.miscellaneous && (
          <div className="card-body p-0">
            {loading.shiftEndSummary ? (
              <div className="text-center my-5">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : filteredData.miscellaneous.length === 0 ? (
              <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt={t("no_data_available")}
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>{t("no_data")}</h3>
                <p>{t("no_miscellaneous_data")}</p>
              </div>
            ) : (
              <Table
                dataSource={filteredData.miscellaneous.map((row, index) => ({
                  ...row,
                  key: `misc-${index}`,
                  net_revenue: (Number(row.revenue) || 0) + (Number(row.tax_amount) || 0) + shiftData.taxTypes.reduce((sum, taxName) => sum + (Number(row[taxName]) || 0), 0),
                }))}
                columns={miscColumns}
                rowKey="key"
                bordered
                pagination={false}
                className="daybook-table"
                style={{ margin: 0 }}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: "#f8f9fa", fontWeight: 600, fontSize: "14px" }}>
                    <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{(miscTotals.revenue || 0).toFixed(2)}</Table.Summary.Cell>
                    {shiftData.taxTypes.map((taxName, index) => (
                      <Table.Summary.Cell key={index}>{currency}{(miscTotals[taxName] || 0).toFixed(2)}</Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell>{currency}{(miscTotals.tax_amount || 0).toFixed(2)}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{(miscTotals.net_revenue || 0).toFixed(2)}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                )}
              />
            )}
          </div>
        )}
      </div>
    </div>
  </div>
)}
      {(!categoryFilter || categoryFilter.value === "all" || categoryFilter.value === "tax") && (
        <div className="row g-0 mb-4">
          <div className="col-md-12">
            <div
              className="card"
              style={{
                borderRadius: "0",
                overflowX: "auto",
                border: "1px solid #dee2e6",
                borderBottom: "none",
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: "#fff",
                  padding: "12px 16px",
                  borderBottom: "none",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("tax")}
              >
                <h5
                  className="mb-0 d-flex justify-content-between align-items-center"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("tax")}
                  {expandedSections.tax ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </h5>
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#007bff",
                  }}
                />
              </div>
              {expandedSections.tax && (
                <div className="card-body p-0">
                  {loading.dailyFlash ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <Table
                      dataSource={[
                        { key: "totalTax", particular: t("total_tax"), ...filteredData.tax },
                        { key: "serviceCharge", particular: t("service_charge"), ...filteredData.tax },
                        { key: "subTotal", particular: t("sub_total"), ...filteredData.tax },
                      ]}
                      columns={[
                        {
                          title: t("particular"),
                          dataIndex: "particular",
                          key: "particular",
                        },
                        {
                          title: t("front_office"),
                          key: "frontOffice",
                          render: (_, record) => `${currency}${record.frontOffice[record.key].toFixed(2)}`,
                        },
                        {
                          title: t("restaurant"),
                          key: "restaurant",
                          render: (_, record) => `${currency}${record.restaurant[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS2",
                          key: "pos2",
                          render: (_, record) => `${currency}${record.pos2[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS3",
                          key: "pos3",
                          render: (_, record) => `${currency}${record.pos3[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS4",
                          key: "pos4",
                          render: (_, record) => `${currency}${record.pos4[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS5",
                          key: "pos5",
                          render: (_, record) => `${currency}${record.pos5[record.key].toFixed(2)}`,
                        },
                        {
                          title: "POS6",
                          key: "pos6",
                          render: (_, record) => `${currency}${record.pos6[record.key].toFixed(2)}`,
                        },
                        {
                          title: t("total"),
                          key: "total",
                          render: (_, record) => `${currency}${record.total[record.key].toFixed(2)}`,
                        },
                      ]}
                      rowKey={(record) => record.key}
                      bordered
                      pagination={false}
                      className="daybook-table"
                      style={{ margin: 0 }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {(!categoryFilter || categoryFilter.value === "all" || categoryFilter.value === "guestMovements") && (
        <div className="row g-0 mb-4">
          <div className="col-md-12">
            <div
              className="card"
              style={{
                borderRadius: "0",
                overflowX: "auto",
                border: "1px solid #dee2e6",
                borderBottom: "none",
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: "#fff",
                  padding: "12px 16px",
                  borderBottom: "none",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("guestMovements")}
              >
                <h5
                  className="mb-0 d-flex justify-content-between align-items-center"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("guest_movements")}
                  {expandedSections.guestMovements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </h5>
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#007bff",
                  }}
                />
              </div>
              {expandedSections.guestMovements && (
                <div className="card-body p-0">
                  {loading.shiftEndSummary ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <Table
                      dataSource={[
                        { key: "checkIns", particular: t("check_ins"), count: shiftData.frontOfficeActivities.guestMovements.checkIns },
                        { key: "checkOuts", particular: t("check_outs"), count: shiftData.frontOfficeActivities.guestMovements.checkOuts },
                        { key: "noShows", particular: t("no_shows"), count: shiftData.frontOfficeActivities.guestMovements.noShows },
                        { key: "cancellations", particular: t("cancellations"), count: shiftData.frontOfficeActivities.guestMovements.cancellations },
                        { key: "stayover", particular: t("stayovers"), count: shiftData.frontOfficeActivities.guestMovements.stayover },
                      ]}
                      columns={[
                        {
                          title: t("activity"),
                          dataIndex: "particular",
                          key: "particular",
                        },
                        {
                          title: t("count"),
                          dataIndex: "count",
                          key: "count",
                        },
                      ]}
                      rowKey={(record) => record.key}
                      bordered
                      pagination={false}
                      className="daybook-table"
                      style={{ margin: 0 }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {(!categoryFilter || categoryFilter.value === "all" || categoryFilter.value === "financialSummary") && (
        <div className="row g-0 mb-4">
          <div className="col-md-12">
            <div
              className="card"
              style={{
                borderRadius: "0",
                overflowX: "auto",
                border: "1px solid #dee2e6",
                borderBottom: "none",
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: "#fff",
                  padding: "12px 16px",
                  borderBottom: "none",
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("financialSummary")}
              >
                <h5
                  className="mb-0 d-flex justify-content-between align-items-center"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {t("financial_summary")}
                  {expandedSections.financialSummary ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </h5>
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#007bff",
                  }}
                />
              </div>
              {expandedSections.financialSummary && (
                <div className="card-body p-0">
                  {loading.shiftEndSummary ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Table
                        dataSource={shiftData.financialSummary.payments.map((payment, index) => ({
                          key: `payment-${index}`,
                          particular: payment.type,
                          amount: `${currency}${payment.amount.toFixed(2)}`,
                        }))}
                        columns={[
                          {
                            title: t("payment_type"),
                            dataIndex: "particular",
                            key: "particular",
                          },
                          {
                            title: t("amount"),
                            dataIndex: "amount",
                            key: "amount",
                          },
                        ]}
                        rowKey={(record) => record.key}
                        bordered
                        pagination={false}
                        className="daybook-table"
                        style={{ margin: 0 }}
                        title={() => (
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#333",
                              padding: "12px 16px",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            {t("payments")}
                          </div>
                        )}
                      />
                      <Table
                        dataSource={[
                          {
                            key: "refundsAdjustments",
                            particular: t("refunds_adjustments"),
                            amount: `${currency}${shiftData.financialSummary.refundsAdjustments.toFixed(2)}`,
                          },
                          {
                            key: "pendingPayments",
                            particular: t("pending_payments"),
                            amount: `${currency}${shiftData.financialSummary.pendingPayments.toFixed(2)}`,
                          },
                        ]}
                        columns={[
                          {
                            title: t("category"),
                            dataIndex: "particular",
                            key: "particular",
                          },
                          {
                            title: t("amount"),
                            dataIndex: "amount",
                            key: "amount",
                          },
                        ]}
                        rowKey={(record) => record.key}
                        bordered
                        pagination={false}
                        className="daybook-table"
                        style={{ margin: 0 }}
                        title={() => (
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#333",
                              padding: "12px 16px",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            {t("refunds_adjustments_and_pending_payments")}
                          </div>
                        )}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className="row g-0"
        style={{
          backgroundColor: "#e6f4ea",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          padding: "15px",
        }}
      >
<div
  className="row g-0"
  style={{
    backgroundColor: "#e6f4ea",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    padding: "15px",
  }}
>
  <div className="col-md-12 text-right">
    <h5
      style={{
        fontSize: "14px",
        fontWeight: 600,
        color: "#333",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        margin: 0,
      }}
    >
      {t("grand_total")}: {currency}{(filteredData.grandTotal || 0).toFixed(2)}
    </h5>
  </div>
</div>
      </div>
    </div>
  </div>
  <style jsx>{`
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
    .table th,
    .table td {
      padding: 8px 12px;
      vertical-align: middle;
      border: 1px solid #ced4da;
    }
    .daybook-table .ant-table-thead > tr > th {
      background: #ffffff;
      font-weight: 600;
      padding: 8px 16px;
      font-size: 14px;
    }
    .daybook-table .ant-table-tbody > tr > td {
      padding: 12px 18px;
      font-size: 14px;
      height: 60px;
    }
    .daybook-table .ant-table {
      border: 1px solid #dee2e6;
    }
    .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
      background: #f5f5f5;
    }
    .uniform-table th,
    .uniform-table td {
      width: 50%;
      padding: 8px 12px;
      vertical-align: middle;
      border: 1px solid #ced4da;
    }
    .table tbody tr:nth-child(odd) {
      background-color: #fff;
    }
    .table tbody tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    .table tbody tr:hover {
      background-color: #f1f3f5;
      transition: background-color 0.2s;
    }
    .react-datepicker-wrapper {
      display: inline-block;
      width: 220px;
    }
    .react-datepicker__input-container input {
      height: 38px;
      font-size: 15px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      padding: 6px 12px;
      background-color: #ffffff;
      width: 100%;
      text-align: center;
      font-weight: bold;
    }
    .loader {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .dot {
      width: 10px;
      height: 10px;
      background-color: #007bff;
      border-radius: 50%;
      margin: 0 5px;
      animation: dot-bounce 1.4s infinite ease-in-out both;
    }
    .dot:nth-child(1) {
      animation-delay: -0.32s;
    }
    .dot:nth-child(2) {
      animation-delay: -0.16s;
    }
    @keyframes dot-bounce {
      0%, 80%, 100% {
        transform: scale(0);
      }
      40% {
        transform: scale(1);
      }
    }
    .stats-box {
      transition: all 0.3s ease;
    }
    .stats-box:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    .stats-content h2 {
      color: #333;
    }
    .stats-content p {
      text-transform: lowercase;
    }
  `}</style>
</div>
  );
};

export default DailyFlashReport;